"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var roosterjs_editor_dom_1 = require("roosterjs-editor-dom");
// Max stack size that cannot be exceeded. When exceeded, old undo history will be dropped
// to keep size under limit. This is kept at 10MB
var MAX_SIZE_LIMIT = 1e7;
/**
 * @internal
 * Provides snapshot based undo service for Editor
 */
var UndoPlugin = /** @class */ (function () {
    /**
     * Construct a new instance of UndoPlugin
     * @param options The wrapper of the state object
     */
    function UndoPlugin(options) {
        this.state = {
            snapshotsService: options.undoSnapshotService || createUndoSnapshots(),
            isRestoring: false,
            hasNewContent: false,
            isNested: false,
            autoCompletePosition: null,
        };
    }
    /**
     * Get a friendly name of  this plugin
     */
    UndoPlugin.prototype.getName = function () {
        return 'Undo';
    };
    /**
     * Initialize this plugin. This should only be called from Editor
     * @param editor Editor instance
     */
    UndoPlugin.prototype.initialize = function (editor) {
        this.editor = editor;
    };
    /**
     * Dispose this plugin
     */
    UndoPlugin.prototype.dispose = function () {
        this.editor = null;
    };
    /**
     * Get plugin state object
     */
    UndoPlugin.prototype.getState = function () {
        return this.state;
    };
    /**
     * Check if the plugin should handle the given event exclusively.
     * @param event The event to check
     */
    UndoPlugin.prototype.willHandleEventExclusively = function (event) {
        return (event.eventType == 0 /* KeyDown */ &&
            event.rawEvent.which == 8 /* BACKSPACE */ &&
            this.canUndoAutoComplete());
    };
    /**
     * Handle events triggered from editor
     * @param event PluginEvent object
     */
    UndoPlugin.prototype.onPluginEvent = function (event) {
        // if editor is in IME, don't do anything
        if (!this.editor || this.editor.isInIME()) {
            return;
        }
        switch (event.eventType) {
            case 11 /* EditorReady */:
                var undoState = this.editor.getUndoState();
                if (!undoState.canUndo && !undoState.canRedo) {
                    // Only add initial snapshot when there is no existing snapshot
                    // Otherwise preserved undo/redo state may be ruined
                    this.addUndoSnapshot();
                }
                break;
            case 0 /* KeyDown */:
                this.onKeyDown(event.rawEvent);
                break;
            case 1 /* KeyPress */:
                this.onKeyPress(event.rawEvent);
                break;
            case 4 /* CompositionEnd */:
                this.clearRedoForInput();
                this.addUndoSnapshot();
                break;
            case 7 /* ContentChanged */:
                if (!this.state.isRestoring) {
                    this.clearRedoForInput();
                }
                break;
        }
    };
    UndoPlugin.prototype.onKeyDown = function (evt) {
        // Handle backspace/delete when there is a selection to take a snapshot
        // since we want the state prior to deletion restorable
        if (evt.which == 8 /* BACKSPACE */ || evt.which == 46 /* DELETE */) {
            if (evt.which == 8 /* BACKSPACE */ && this.canUndoAutoComplete()) {
                evt.preventDefault();
                this.editor.undo();
                this.state.autoCompletePosition = null;
                this.lastKeyPress = evt.which;
            }
            else {
                var selectionRange = this.editor.getSelectionRange();
                // Add snapshot when
                // 1. Something has been selected (not collapsed), or
                // 2. It has a different key code from the last keyDown event (to prevent adding too many snapshot when keeping press the same key), or
                // 3. Ctrl/Meta key is pressed so that a whole word will be deleted
                if (selectionRange &&
                    (!selectionRange.collapsed ||
                        this.lastKeyPress != evt.which ||
                        roosterjs_editor_dom_1.isCtrlOrMetaPressed(evt))) {
                    this.addUndoSnapshot();
                }
                // Since some content is deleted, always set hasNewContent to true so that we will take undo snapshot next time
                this.state.hasNewContent = true;
                this.lastKeyPress = evt.which;
            }
        }
        else if (evt.which >= 33 /* PAGEUP */ && evt.which <= 40 /* DOWN */) {
            // PageUp, PageDown, Home, End, Left, Right, Up, Down
            if (this.state.hasNewContent) {
                this.addUndoSnapshot();
            }
            this.lastKeyPress = 0;
        }
    };
    UndoPlugin.prototype.onKeyPress = function (evt) {
        if (evt.metaKey) {
            // if metaKey is pressed, simply return since no actual effect will be taken on the editor.
            // this is to prevent changing hasNewContent to true when meta + v to paste on Safari.
            return;
        }
        var range = this.editor.getSelectionRange();
        if ((range && !range.collapsed) ||
            (evt.which == 32 /* SPACE */ && this.lastKeyPress != 32 /* SPACE */) ||
            evt.which == 13 /* ENTER */) {
            this.addUndoSnapshot();
            if (evt.which == 13 /* ENTER */) {
                // Treat ENTER as new content so if there is no input after ENTER and undo,
                // we restore the snapshot before ENTER
                this.state.hasNewContent = true;
            }
        }
        else {
            this.clearRedoForInput();
        }
        this.lastKeyPress = evt.which;
    };
    UndoPlugin.prototype.clearRedoForInput = function () {
        this.state.snapshotsService.clearRedo();
        this.lastKeyPress = 0;
        this.state.hasNewContent = true;
    };
    UndoPlugin.prototype.canUndoAutoComplete = function () {
        var _a;
        return (this.state.snapshotsService.canUndoAutoComplete() && ((_a = this.state.autoCompletePosition) === null || _a === void 0 ? void 0 : _a.equalTo(this.editor.getFocusedPosition())));
    };
    UndoPlugin.prototype.addUndoSnapshot = function () {
        this.editor.addUndoSnapshot();
        this.state.autoCompletePosition = null;
    };
    return UndoPlugin;
}());
exports.default = UndoPlugin;
function createUndoSnapshots() {
    var snapshots = roosterjs_editor_dom_1.createSnapshots(MAX_SIZE_LIMIT);
    return {
        canMove: function (delta) { return roosterjs_editor_dom_1.canMoveCurrentSnapshot(snapshots, delta); },
        move: function (delta) { return roosterjs_editor_dom_1.moveCurrentSnapshot(snapshots, delta); },
        addSnapshot: function (snapshot, isAutoCompleteSnapshot) {
            return roosterjs_editor_dom_1.addSnapshot(snapshots, snapshot, isAutoCompleteSnapshot);
        },
        clearRedo: function () { return roosterjs_editor_dom_1.clearProceedingSnapshots(snapshots); },
        canUndoAutoComplete: function () { return roosterjs_editor_dom_1.canUndoAutoComplete(snapshots); },
    };
}
//# sourceMappingURL=UndoPlugin.js.map